/**
 * Libraries
 */

import type { RouteLocationNormalized } from 'vue-router';

/**
 * Utils
 */

import { loadTicketByCode } from '@core/middleware/ticketLoad';

import { createTicketAccessContext } from '@modules/tickets/access/ticketAccessContext';
import {
  isReachableTicketRouteName,
  isTicketRouteAllowed,
} from '@modules/tickets/access/ticketRouteAccess';

import { buildTicketRouteLocation } from '@modules/tickets/composables/actions/resolveTicketRedirect';

/**
 * Stores
 */

import { useTicketStore } from '@/stores/ticket';

/**
 * Types
 */

import type { RoutesList } from '@core/router/routeNames';
import { routeTicketEnum } from '@/core/router/routeNames';
import type { Ticket } from '@generatedTypes';
import type { RedirectRouteLocation } from '@modules/tickets/composables/actions/resolveTicketRedirect';
import type { TicketLoadFailure } from '@core/middleware/ticketLoad';

type TicketNavigationResult =
  | { status: 'allowed' }
  | { status: 'redirect'; location: RedirectRouteLocation }
  | { status: 'route_denied' }
  | { status: 'forbidden' }
  | { status: 'not_found' }
  | { status: 'technical'; cause: unknown; retryFullPath: string };

type TicketNavigationGuardOutcome = Exclude<
  TicketNavigationResult,
  { status: 'allowed' }
>;

/**
 * Runs the ticket navigation guard.
 * @returns A navigation outcome, or undefined when the target route is allowed.
 */
export async function runTicketNavigationGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  isTicketUser: boolean,
): Promise<TicketNavigationGuardOutcome | undefined> {
  if (!isTicketUser) return { status: 'forbidden' };

  if (to.meta.type !== 'ticket') return undefined;

  const ticketCode = extractTicketCode(to);
  if (!ticketCode) return undefined;

  const shouldFetchFromNetwork = !isSameTicketContext(from, to);

  const result = await resolveTicketNavigationState(
    ticketCode,
    to.name as RoutesList,
    shouldFetchFromNetwork,
  );

  return mapResultToNavigationOutcome(result, to.fullPath);
}

/**
 * Resolves the ticket navigation state.
 */
async function resolveTicketNavigationState(
  ticketCode: string,
  targetRouteName: RoutesList,
  shouldFetchFromNetwork: boolean,
): Promise<TicketNavigationResult | TicketLoadFailure> {
  const loadResult = await loadTicketByCode(ticketCode, shouldFetchFromNetwork);

  if (loadResult.status !== 'loaded') {
    return loadResult;
  }

  syncTicketStore(loadResult.ticket);

  return validateTicketLinkAccess(loadResult.ticket, targetRouteName);
}

/**
 * Validates the ticket link access.
 */
function validateTicketLinkAccess(
  ticket: Ticket,
  targetRouteName: RoutesList,
): TicketNavigationResult {
  if (!isReachableTicketRouteName(targetRouteName)) {
    return { status: 'route_denied' };
  }

  const context = createTicketAccessContext(ticket);

  if (!isTicketRouteAllowed(context, targetRouteName)) {
    return {
      status: 'redirect',
      location: buildTicketRouteLocation(
        routeTicketEnum.TICKET_INFO,
        ticket.code,
      ),
    };
  }

  return { status: 'allowed' };
}

/**
 * Syncs the ticket store with the ticket.
 * @param ticket - The ticket to sync.
 */
function syncTicketStore(ticket: Ticket): void {
  const ticketStore = useTicketStore();
  ticketStore.setCurrentTicket(ticket);
  ticketStore.setCurrentTicketCode(ticket.code);
}

/**
 * Checks if the navigation remains in the same ticket context.
 */
function isSameTicketContext(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized,
): boolean {
  const isFromTicket = from.meta?.type === 'ticket';
  const isToTicket = to.meta?.type === 'ticket';

  const fromCode = extractTicketCode(from);
  const toCode = extractTicketCode(to);

  return Boolean(isFromTicket && isToTicket && fromCode && fromCode === toCode);
}

/**
 * Extracts the ticket code from a route.
 */
function extractTicketCode(route: RouteLocationNormalized): string | undefined {
  const code = (route.params as Record<string, string | string[]>).code;
  return code ? String(code) : undefined;
}

/**
 * Maps an internal navigation result to a guard outcome (undefined = allow).
 */
function mapResultToNavigationOutcome(
  result: TicketNavigationResult | TicketLoadFailure,
  retryFullPath: string,
): TicketNavigationGuardOutcome | undefined {
  if (result.status === 'allowed') {
    return undefined;
  }

  if (result.status === 'technical') {
    return {
      status: 'technical',
      cause: result.cause,
      retryFullPath,
    };
  }

  return result;
}

export type { TicketNavigationResult, TicketNavigationGuardOutcome };
